import { $api } from "@/utils/fetch.ts";
import type { OrgModelsSaveCallbacks } from "@/services/org-models-service.ts";
import { useQueryClient } from "@tanstack/react-query";

export const getOrgImageModelsQueryOptions = () =>
	$api.queryOptions("get", "/api/orgs/current/image-models");

export const imageModelAccessQueryOptions = () =>
	$api.queryOptions("get", "/api/orgs/current/image-models/access");

/**
 * Sets who may use one image model - the whole org or specific teams. The
 * twin of `useUpdateModelAccessMutation`: the "org" level writes an
 * `org_image_models` row, so the org configuration is re-read along with the
 * access map, and so is the member-facing list both of them decide.
 */
export const useUpdateImageModelAccessMutation = (
	callbacks: OrgModelsSaveCallbacks = {},
) => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/orgs/current/image-models/access", {
		// Shares the whole-set replace's scope: both rewrite the image selection.
		scope: { id: "org-image-models-config" },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: imageModelAccessQueryOptions().queryKey,
			});
			queryClient.invalidateQueries({
				queryKey: getOrgImageModelsQueryOptions().queryKey,
			});
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/image-models"],
			});
			callbacks.onSuccess?.();
		},
		onError: (error) => {
			queryClient.invalidateQueries({
				queryKey: imageModelAccessQueryOptions().queryKey,
			});
			callbacks.onError?.(error);
		},
	});
};

/** See `OrgModelsSaveCallbacks` — same split, for the image-model section. */
export interface OrgImageModelsSaveCallbacks {
	onSuccess?: () => void;
	onError?: () => void;
}

export const useUpdateOrgImageModelsMutation = (
	callbacks: OrgImageModelsSaveCallbacks = {},
) => {
	const queryClient = useQueryClient();
	return $api.useMutation("put", "/api/orgs/current/image-models", {
		// See org-models-service.ts for why the scope and why these callbacks sit
		// on the mutation rather than on the mutate() call.
		scope: { id: "org-image-models-config" },
		onSuccess: (data) => {
			queryClient.setQueryData(getOrgImageModelsQueryOptions().queryKey, data);
			// The member-facing image model list is derived from this selection.
			queryClient.invalidateQueries({
				queryKey: ["get", "/api/image-models"],
			});
			callbacks.onSuccess?.();
		},
		onError: () => {
			queryClient.invalidateQueries({
				queryKey: getOrgImageModelsQueryOptions().queryKey,
			});
			callbacks.onError?.();
		},
	});
};
